import React, { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CurrencyInr, Copy, WhatsappLogo, ChatTeardropText, Link as LinkIcon, CheckCircle, Hourglass, FilePdf, ClockCounterClockwise, CalendarBlank, Tag, X } from "@phosphor-icons/react";
import InstallmentEditor from "./InstallmentEditor";

/**
 * PaymentPanel — admin-side panel inside the registration dialog for approved applicants.
 * Generates / shows the public /pay/<token> URL and provides share helpers (copy, WhatsApp, SMS).
 * Once the applicant marks paid, admin can verify + mark the student as Enrolled.
 */
export default function PaymentPanel({ registration, onChanged }) {
  const { user: me } = useAuth();
  const [reg, setReg] = useState(registration);
  const [busy, setBusy] = useState(false);
  const [showInstallments, setShowInstallments] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);

  const installments = reg.installments || [];

  const fee = Number(reg.payment_amount || 0);
  const discount = Number(reg.discount_amount || 0);
  const net = Math.max(0, fee - discount);

  const canEditDiscount =
    (me?.role === "admin" || me?.role === "manager") &&
    reg.status !== "enrolled" &&
    !(reg.payment_history && reg.payment_history.length > 0) &&
    !installments.some((i) => i.status === "verified");

  const verifyInstallment = async (number) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/installments/${number}/verify`);
      setReg(data);
      toast.success(`Instalment ${number} verified`);
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const payUrl = useMemo(() => {
    if (!reg.payment_token) return "";
    return `${window.location.origin}/pay/${reg.payment_token}`;
  }, [reg.payment_token]);

  const requestPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/payment/request`);
      setReg(data);
      toast.success("Payment link generated");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const enroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/enroll`);
      setReg(data);
      toast.success(`${data.full_name} is now enrolled`);
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const verifyCash = async (number = null) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/registrations/${reg.id}/verify-cash-payment`,
        number ? { installment_number: number } : {}
      );
      setReg(data);
      toast.success(number ? `Cash instalment #${number} verified` : `${data.full_name} is now enrolled (cash)`);
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Cash verification failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const receiptUrl = useMemo(() => {
    if (!reg.payment_token || reg.status !== "enrolled") return "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/payments/${reg.payment_token}/receipt`;
  }, [reg.payment_token, reg.status]);

  const phoneClean = (reg.phone || "").replace(/[^0-9]/g, "");
  const message = `Hi ${reg.full_name?.split(" ")[0] || ""}, your application for ${reg.course_name || "the programme"} is approved. Pay your tuition (₹${Number(net).toLocaleString("en-IN")}) here: ${payUrl}`;
  const waUrl = phoneClean ? `https://wa.me/${phoneClean}?text=${encodeURIComponent(message)}` : "";
  const smsUrl = phoneClean ? `sms:${phoneClean}?&body=${encodeURIComponent(message)}` : "";

  if (reg.status !== "approved" && reg.status !== "enrolled") {
    return null;
  }

  const removeDiscount = async () => {
    if (!window.confirm("Remove the applied discount?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/registrations/${reg.id}/discount`);
      setReg(data);
      toast.success("Discount removed");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 p-5 bg-slate-50/40" data-testid="payment-panel">
      <div className="flex items-center gap-2 mb-4">
        <CurrencyInr size={18} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Tuition payment</div>
        <PaymentStatusBadge status={reg.payment_status} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {discount > 0 ? (
          <>
            <Stat label="Course fee" value={`₹ ${fee.toLocaleString("en-IN")}`} />
            <Stat label={`Discount (${reg.discount_input_type || "—"})`} value={`− ₹ ${discount.toLocaleString("en-IN")}`} />
            <Stat label="Net amount due" value={`₹ ${net.toLocaleString("en-IN")}`} />
          </>
        ) : (
          <Stat label="Amount due" value={`₹ ${net.toLocaleString("en-IN")}`} />
        )}
        <Stat label="Phone on file" value={reg.phone || "—"} />
        {reg.payment_utr && <Stat label="UTR" value={reg.payment_utr} mono />}
        {reg.payment_paid_at && <Stat label="Paid at" value={new Date(reg.payment_paid_at).toLocaleString()} />}
      </div>

      {/* Discount applied chip */}
      {discount > 0 && (
        <div className="mb-4 px-3 py-2 border border-[var(--sms-primary)]/30 bg-[var(--sms-primary)]/5 flex items-start gap-3" data-testid="discount-chip">
          <Tag size={16} weight="duotone" className="text-[var(--sms-primary)] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0 text-xs">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">
              Discount applied
              {reg.discount_input_type === "percent" && reg.discount_input_value
                ? ` · ${reg.discount_input_value}%`
                : ""}
            </div>
            <div className="text-slate-700 mt-1">"{reg.discount_reason}"</div>
            <div className="text-slate-400 mt-0.5">
              by {reg.discount_by_email} ({reg.discount_by_role}) · {reg.discount_at && new Date(reg.discount_at).toLocaleString()}
            </div>
          </div>
          {canEditDiscount && (
            <button onClick={removeDiscount} disabled={busy} data-testid="btn-remove-discount" className="text-slate-400 hover:text-[var(--sms-danger)] shrink-0" title="Remove discount">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
      )}

      {!reg.payment_token && reg.status === "approved" && (
        <div className="flex flex-wrap gap-2">
          <button onClick={requestPayment} disabled={busy} data-testid="btn-request-payment" className="sms-btn-primary inline-flex items-center gap-2">
            <LinkIcon size={14} weight="bold" /> {busy ? "Generating…" : "Generate payment link"}
          </button>
          {canEditDiscount && discount === 0 && (
            <button onClick={() => setShowDiscount(true)} data-testid="btn-apply-discount" className="sms-btn-secondary inline-flex items-center gap-2">
              <Tag size={14} weight="duotone" /> Apply discount
            </button>
          )}
        </div>
      )}

      {/* Apply discount also when payment link exists but no UTR claimed yet */}
      {reg.payment_token && reg.status !== "enrolled" && canEditDiscount && discount === 0 && (
        <button onClick={() => setShowDiscount(true)} data-testid="btn-apply-discount" className="sms-btn-secondary inline-flex items-center gap-2 text-xs mb-3">
          <Tag size={14} weight="duotone" /> Apply discount
        </button>
      )}

      {showDiscount && (
        <DiscountModal
          reg={reg}
          onClose={() => setShowDiscount(false)}
          onApplied={(updated) => {
            setReg(updated);
            setShowDiscount(false);
            onChanged?.(updated);
          }}
        />
      )}

      {reg.payment_token && reg.status !== "enrolled" && (
        <div className="space-y-3">
          <div className="border border-slate-300 bg-white p-3 flex items-center gap-2">
            <div className="flex-1 min-w-0 font-mono-sms text-xs truncate" data-testid="payment-share-url">{payUrl}</div>
            <button onClick={copy} data-testid="copy-pay-url" className="text-slate-500 hover:text-[var(--sms-primary)]" title="Copy link">
              <Copy size={14} weight="bold" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {waUrl && (
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="sms-btn-secondary inline-flex items-center gap-2 text-xs">
                <WhatsappLogo size={14} weight="bold" /> WhatsApp
              </a>
            )}
            {smsUrl && (
              <a href={smsUrl} data-testid="share-sms" className="sms-btn-secondary inline-flex items-center gap-2 text-xs">
                <ChatTeardropText size={14} weight="bold" /> SMS
              </a>
            )}
            <a href={payUrl} target="_blank" rel="noopener noreferrer" data-testid="open-pay-page" className="sms-btn-secondary inline-flex items-center gap-2 text-xs">
              <LinkIcon size={14} weight="bold" /> Open page
            </a>
          </div>

          {reg.payment_status === "claimed" && (
            <div className="mt-3 p-3 border border-amber-300 bg-amber-50 flex items-start gap-3">
              <Hourglass size={18} weight="duotone" className="text-amber-700 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-amber-700">
                  Payment claimed{reg.payment_method ? ` · ${methodLabel(reg.payment_method)}` : ""}
                </div>
                <div className="text-sm mt-1">
                  {reg.payment_method === "cash"
                    ? "Cash payment — collect at the counter, then verify here."
                    : reg.payment_method === "bank_transfer"
                    ? "Check the bank statement for the transaction, then verify."
                    : "Verify the receipt in your UPI app, then enroll."}
                </div>
              </div>
              {installments.length === 0 && (
                <button onClick={reg.payment_method === "cash" ? verifyCash : enroll} disabled={busy} data-testid={reg.payment_method === "cash" ? "btn-verify-cash" : "btn-mark-enrolled"} className="sms-btn-primary text-xs">
                  <CheckCircle size={14} weight="bold" className="inline mr-1" />
                  {reg.payment_method === "cash" ? "Verify cash" : "Verify & Enroll"}
                </button>
              )}
            </div>
          )}

          {/* Instalment editor toggle */}
          <div className="mt-3">
            <button onClick={() => setShowInstallments((v) => !v)} data-testid="btn-toggle-installments" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
              <CalendarBlank size={12} weight="bold" />
              {installments.length === 0 ? "Configure instalments" : `Edit schedule (${installments.length})`}
            </button>
          </div>

          {showInstallments && (
            <InstallmentEditor
              registration={reg}
              onClose={() => setShowInstallments(false)}
              onSaved={(updated) => {
                setReg(updated);
                setShowInstallments(false);
                onChanged?.(updated);
              }}
            />
          )}

          {/* Instalment table when schedule exists */}
          {installments.length > 0 && (
            <div className="mt-3 border border-slate-200" data-testid="installments-list">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Due</th>
                    <th className="text-left px-3 py-2">UTR</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2"> </th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((it) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const overdue = it.status !== "verified" && it.due_date && it.due_date < today;
                    const phoneClean2 = (reg.phone || "").replace(/[^0-9]/g, "");
                    const reminderMsg = `Hi ${reg.full_name?.split(" ")[0] || ""}, instalment #${it.number} of ₹${Number(it.amount).toLocaleString("en-IN")} for ${reg.course_name || "your programme"} was due on ${it.due_date}. Pay here: ${payUrl}`;
                    const waReminder = phoneClean2 ? `https://wa.me/${phoneClean2}?text=${encodeURIComponent(reminderMsg)}` : "";
                    return (
                      <tr key={it.number} data-testid={`installment-${it.number}`} className={`border-t border-slate-200 ${overdue ? "bg-red-50/40" : ""}`}>
                        <td className="px-3 py-2 font-mono-sms">{it.number}</td>
                        <td className="px-3 py-2 text-right font-mono-sms">₹{Number(it.amount).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 font-mono-sms text-slate-500">{it.due_date}</td>
                        <td className="px-3 py-2 font-mono-sms">{it.utr || "—"}</td>
                        <td className="px-3 py-2">
                          {overdue ? (
                            <span className="sms-badge sms-badge-rejected" data-testid={`installment-${it.number}-overdue`}>Overdue</span>
                          ) : (
                            <span className={`sms-badge ${it.status === "verified" ? "sms-badge-approved" : it.status === "claimed" ? "sms-badge-pending" : "sms-badge-pending"}`}>{it.status}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-3">
                            {overdue && waReminder && (
                              <a href={waReminder} target="_blank" rel="noopener noreferrer" data-testid={`remind-installment-${it.number}`} className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline">
                                Remind
                              </a>
                            )}
                            {it.status === "claimed" && (() => {
                              const lastEntry = (it.history && it.history[it.history.length - 1]) || {};
                              const isCash = lastEntry.method === "cash";
                              return (
                                <button
                                  onClick={() => (isCash ? verifyCash(it.number) : verifyInstallment(it.number))}
                                  disabled={busy}
                                  data-testid={`verify-installment-${it.number}`}
                                  className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline"
                                >
                                  {isCash ? "Verify cash" : "Verify"}
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {reg.status === "enrolled" && (
        <div className="space-y-3">
          <div className="p-3 bg-[var(--sms-success)]/5 border border-[var(--sms-success)]/30 flex items-center gap-2 text-[#166534]">
            <CheckCircle size={18} weight="duotone" />
            <div className="font-mono-sms text-xs uppercase tracking-[0.15em]">
              Enrolled · {reg.enrolled_at && new Date(reg.enrolled_at).toLocaleString()}
            </div>
          </div>
          {receiptUrl && (
            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={reg.payment_method === "cash" ? "btn-download-cash-receipt" : "btn-download-receipt"}
              className="sms-btn-primary inline-flex items-center gap-2"
            >
              <FilePdf size={16} weight="duotone" />
              {reg.payment_method === "cash"
                ? "Print cash receipt"
                : reg.payment_method === "bank_transfer"
                ? "Download bank receipt"
                : "Download PDF receipt"}
            </a>
          )}
        </div>
      )}

      {/* Payment history (audit trail) */}
      {Array.isArray(reg.payment_history) && reg.payment_history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={14} weight="duotone" className="text-slate-500" />
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Payment attempts ({reg.payment_history.length})
            </div>
          </div>
          <ul className="space-y-1.5" data-testid="payment-history">
            {reg.payment_history.map((h, i) => (
              <li key={i} className="flex items-center gap-3 text-xs">
                <span className="font-mono-sms text-slate-400 w-4 text-right">{i + 1}.</span>
                <span className="font-mono-sms text-slate-700">{h.utr}</span>
                <span className="text-slate-500 truncate flex-1">{h.note || "—"}</span>
                <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  {h.claimed_at && new Date(h.claimed_at).toLocaleString()}
                </span>
                {h.status === "verified" && <CheckCircle size={12} weight="duotone" className="text-[var(--sms-success)]" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono-sms text-sm" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function methodLabel(method) {
  if (method === "cash") return "Cash";
  if (method === "bank_transfer") return "Bank transfer";
  return "UPI";
}

function PaymentStatusBadge({ status }) {
  const map = {
    not_requested: { cls: "sms-badge-pending", txt: "Not requested" },
    requested: { cls: "sms-badge-pending", txt: "Requested" },
    claimed: { cls: "sms-badge-pending", txt: "Claimed" },
    verified: { cls: "sms-badge-approved", txt: "Verified" },
  };
  const m = map[status] || map.not_requested;
  return <span className={`sms-badge ${m.cls}`}>{m.txt}</span>;
}

function DiscountModal({ reg, onClose, onApplied }) {
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const fee = Number(reg.payment_amount || 0);
  const numericValue = Number(value || 0);
  const computed =
    type === "percent"
      ? Math.round((fee * numericValue) / 100 * 100) / 100
      : numericValue;
  const net = Math.max(0, fee - computed);
  const overFee = type === "fixed" && numericValue > fee;
  const overPercent = type === "percent" && numericValue > 100;
  const valid = numericValue > 0 && reason.trim() && !overFee && !overPercent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/discount`, {
        input_type: type,
        input_value: numericValue,
        reason: reason.trim(),
      });
      toast.success(`Discount applied · net ₹${(Number(data.payment_amount) - Number(data.discount_amount || 0)).toLocaleString("en-IN")}`);
      onApplied(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to apply discount");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-md p-6" data-testid="discount-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] inline-flex items-center gap-2">
              <Tag size={12} weight="duotone" /> Apply discount
            </div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">{reg.full_name}</div>
            <div className="text-xs text-slate-500 mt-1">{reg.course_name} · Course fee ₹ {fee.toLocaleString("en-IN")}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} weight="bold" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Discount type</div>
            <div className="flex items-center gap-1 border border-slate-200">
              {[
                { id: "percent", label: "Percent (%)" },
                { id: "fixed", label: "Fixed ₹" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setType(t.id); setValue(""); }}
                  data-testid={`discount-type-${t.id}`}
                  className={`flex-1 px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${type === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
              {type === "percent" ? "Percent off (0–100)" : "Discount amount (₹)"} *
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              max={type === "percent" ? 100 : fee}
              required
              data-testid="discount-value"
              className="sms-input font-mono-sms"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === "percent" ? "e.g. 15" : "e.g. 5000"}
            />
            {overPercent && <div className="text-[11px] text-[var(--sms-danger)] mt-1">Percent cannot exceed 100.</div>}
            {overFee && <div className="text-[11px] text-[var(--sms-danger)] mt-1">Discount cannot exceed the fee (₹ {fee.toLocaleString("en-IN")}).</div>}
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Reason *</div>
            <textarea
              rows={2}
              required
              data-testid="discount-reason"
              className="sms-input"
              placeholder="e.g. Sibling discount, scholarship, financial aid review"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
            <div className="flex items-center justify-between"><span className="text-slate-500">Course fee</span><span className="font-mono-sms">₹ {fee.toLocaleString("en-IN")}</span></div>
            <div className="flex items-center justify-between text-[var(--sms-primary)]"><span className="font-mono-sms text-[10px] uppercase tracking-[0.2em]">Discount</span><span className="font-mono-sms" data-testid="discount-preview">− ₹ {Math.max(0, computed).toLocaleString("en-IN")}</span></div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-1 mt-1 font-bold"><span>Net amount due</span><span className="font-mono-sms" data-testid="discount-net-preview">₹ {net.toLocaleString("en-IN")}</span></div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="sms-btn-secondary text-xs">Cancel</button>
          <button type="submit" disabled={!valid || busy} data-testid="confirm-discount" className="sms-btn-primary">
            {busy ? "Applying…" : "Apply discount"}
          </button>
        </div>
      </form>
    </div>
  );
}
